<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainLlmConfigAPI from 'dashboard/api/captain/llmConfig';
import AgentsAPI from 'dashboard/api/agents';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextInput from 'next/input/Input.vue';
import NextButton from 'next/button/Button.vue';

const { t } = useI18n();
const isLoading = ref(true);
const isSaving = ref(false);
const apiKeyConfigured = ref(false);
const canConfigure = ref(false);
const models = ref([]);
const agents = ref([]);
const form = reactive({
  apiKey: '',
  endpoint: 'https://api.openai.com',
  model: 'gpt-4.1-mini',
  autoReplySuggestions: true,
  embeddingModel: 'text-embedding-3-small',
  knowledgeManagerIds: [],
});

const availableAgents = computed(() => agents.value.filter(agent => agent.role === 'agent'));

const loadConfig = async () => {
  try {
    isLoading.value = true;
    const { data } = await CaptainLlmConfigAPI.get();
    apiKeyConfigured.value = data.api_key_configured;
    canConfigure.value = data.can_configure;
    form.endpoint = data.endpoint;
    form.model = data.model;
    form.autoReplySuggestions = data.auto_reply_suggestions;
    form.embeddingModel = data.embedding_model;
    form.knowledgeManagerIds = data.knowledge_manager_ids;
    models.value = data.models;
    if (canConfigure.value) {
      const { data: agentData } = await AgentsAPI.get();
      agents.value = agentData;
    }
  } catch (error) {
    useAlert(t('GPT_SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveConfig = async () => {
  try {
    isSaving.value = true;
    const { data } = await CaptainLlmConfigAPI.update({
      api_key: form.apiKey || undefined,
      endpoint: form.endpoint,
      model: form.model,
      auto_reply_suggestions: form.autoReplySuggestions,
      embedding_model: form.embeddingModel,
      knowledge_manager_ids: form.knowledgeManagerIds,
    });
    apiKeyConfigured.value = data.api_key_configured;
    form.apiKey = '';
    useAlert(t('GPT_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GPT_SETTINGS.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadConfig);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="t('GPT_SETTINGS.LOADING')">
    <template #header>
      <BaseSettingsHeader :title="t('GPT_SETTINGS.TITLE')" :description="t('GPT_SETTINGS.DESCRIPTION')" />
    </template>
    <template #body>
      <SectionLayout
        v-if="canConfigure"
        :title="t('GPT_SETTINGS.CONNECTION.TITLE')"
        :description="t('GPT_SETTINGS.CONNECTION.DESCRIPTION')"
      >
        <form class="grid max-w-2xl gap-5" @submit.prevent="saveConfig">
          <div v-if="apiKeyConfigured" class="rounded-lg bg-n-teal-3 px-4 py-3 text-sm text-n-teal-11">
            {{ t('GPT_SETTINGS.API_KEY.CONFIGURED') }}
          </div>
          <WithLabel name="apiKey" :label="t('GPT_SETTINGS.API_KEY.LABEL')" :help-message="t('GPT_SETTINGS.API_KEY.HELP')">
            <TextInput v-model="form.apiKey" class="w-full" type="password" autocomplete="new-password" :placeholder="t('GPT_SETTINGS.API_KEY.PLACEHOLDER')" />
          </WithLabel>
          <WithLabel name="endpoint" :label="t('GPT_SETTINGS.ENDPOINT.LABEL')" :help-message="t('GPT_SETTINGS.ENDPOINT.HELP')" required>
            <TextInput v-model="form.endpoint" class="w-full" type="url" :placeholder="t('GPT_SETTINGS.ENDPOINT.PLACEHOLDER')" required />
          </WithLabel>
          <WithLabel name="model" :label="t('GPT_SETTINGS.MODEL.LABEL')" :help-message="t('GPT_SETTINGS.MODEL.HELP')" required>
            <select v-model="form.model" class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand" required>
              <option v-for="model in models" :key="model.id" :value="model.id">{{ model.name }} ({{ model.id }})</option>
            </select>
          </WithLabel>
          <WithLabel name="embeddingModel" :label="t('GPT_SETTINGS.EMBEDDING_MODEL.LABEL')" :help-message="t('GPT_SETTINGS.EMBEDDING_MODEL.HELP')">
            <TextInput v-model="form.embeddingModel" class="w-full" />
          </WithLabel>
          <label class="flex items-center gap-3 text-sm text-n-slate-12">
            <input v-model="form.autoReplySuggestions" type="checkbox" class="h-4 w-4" />
            {{ t('GPT_SETTINGS.AUTO_REPLY') }}
          </label>
          <WithLabel name="knowledgeManagers" :label="t('GPT_SETTINGS.KNOWLEDGE_MANAGERS.LABEL')" :help-message="t('GPT_SETTINGS.KNOWLEDGE_MANAGERS.HELP')">
            <select v-model="form.knowledgeManagerIds" multiple class="min-h-28 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-3 text-sm text-n-slate-12">
              <option v-for="agent in availableAgents" :key="agent.id" :value="agent.id">{{ agent.name }} ({{ agent.email }})</option>
            </select>
          </WithLabel>
          <div><NextButton blue type="submit" :is-loading="isSaving" :label="t('GPT_SETTINGS.SAVE')" /></div>
        </form>
      </SectionLayout>

      <div v-else class="p-8 text-sm text-n-slate-10">{{ t('GPT_SETTINGS.NO_ACCESS') }}</div>
    </template>
  </SettingsLayout>
</template>
